import { API_URL } from '../styles/shared';
import React, { useState } from 'react';
import { Share2, BookmarkPlus, ChevronDown, ChevronUp, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Target, BarChart2, Activity, DollarSign, Clock } from 'lucide-react';

// ─── Score Ring ────────────────────────────────────────────────────────────────
const ScoreRing = ({ score }) => {
  const r = 42, circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.min(score / 100, 1));
  const color = score >= 75 ? '#00ff88' : score >= 60 ? '#00d4ff' : score >= 40 ? '#ffaa00' : '#ff4444';
  const label = score >= 75 ? 'Strong' : score >= 60 ? 'Good' : score >= 40 ? 'Neutral' : 'Weak';
  return (
    <div style={{ position: 'relative', width: 110, height: 110, flexShrink: 0 }}>
      <svg width="110" height="110" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="55" cy="55" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
        <circle cx="55" cy="55" r={r} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s ease' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: '1.6rem', fontWeight: 900, color, lineHeight: 1, letterSpacing: '-0.03em' }}>{Math.round(score)}</div>
        <div style={{ fontSize: '0.6rem', color: '#64748b', fontWeight: 700, letterSpacing: '0.06em', marginTop: '2px' }}>{label.toUpperCase()}</div>
      </div>
    </div>
  );
};

// ─── Metric Cell ────────────────────────────────────────────────────────────────
const MetricCell = ({ label, value, sub, highlight, accent }) => (
  <div style={{ padding: '0.85rem 1rem', background: highlight ? 'rgba(0,255,136,0.04)' : accent ? 'rgba(0,212,255,0.03)' : 'rgba(255,255,255,0.02)', borderRadius: '10px', border: `1px solid ${highlight ? 'rgba(0,255,136,0.12)' : accent ? 'rgba(0,212,255,0.1)' : 'rgba(255,255,255,0.05)'}` }}>
    <div style={{ fontSize: '0.65rem', color: '#475569', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: '0.35rem' }}>{label}</div>
    <div style={{ fontSize: '1rem', fontWeight: 800, color: highlight ? '#00ff88' : accent ? '#00d4ff' : '#f8fafc', letterSpacing: '-0.01em' }}>{value || '—'}</div>
    {sub && <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: '0.2rem' }}>{sub}</div>}
  </div>
);

// ─── Metric Section Header ───────────────────────────────────────────────────────
const MetricSection = ({ label }) => (
  <div style={{ gridColumn: '1 / -1', fontSize: '0.62rem', fontWeight: 800, color: '#334155', letterSpacing: '0.1em', textTransform: 'uppercase', paddingTop: '0.5rem', paddingBottom: '0.15rem', borderBottom: '1px solid rgba(255,255,255,0.04)', marginBottom: '0.1rem' }}>
    {label}
  </div>
);

// ─── Signal Bar ────────────────────────────────────────────────────────────────
const SignalBar = ({ label, value, max = 100, color = '#00d4ff', sublabel }) => {
  const pct = Math.min((value / max) * 100, 100);
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', alignItems: 'baseline' }}>
        <div>
          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{label}</span>
          {sublabel && <span style={{ fontSize: '0.6rem', color: '#334155', marginLeft: '0.35rem' }}>{sublabel}</span>}
        </div>
        <span style={{ fontSize: '0.75rem', fontWeight: 700, color }}>{value}</span>
      </div>
      <div style={{ height: '4px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '2px', transition: 'width 0.8s ease' }} />
      </div>
    </div>
  );
};

// ─── NA Signal Bar (ETF/foreign graceful state) ────────────────────────────────
const NASignalBar = ({ label, reason }) => (
  <div style={{ marginBottom: '0.75rem' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
      <span style={{ fontSize: '0.75rem', color: '#475569' }}>{label}</span>
      <span style={{ fontSize: '0.68rem', color: '#334155', fontStyle: 'italic' }}>N/A</span>
    </div>
    <div style={{ height: '4px', background: 'rgba(255,255,255,0.03)', borderRadius: '2px', border: '1px dashed rgba(255,255,255,0.06)' }} />
    {reason && <div style={{ fontSize: '0.6rem', color: '#334155', marginTop: '0.2rem' }}>{reason}</div>}
  </div>
);

// ─── Recommendation Badge ───────────────────────────────────────────────────────
const RecBadge = ({ rec }) => {
  const cfg = {
    BULLISH: { bg: 'linear-gradient(135deg,#00ff88,#00d4aa)', text: '#050d1a', glow: 'rgba(0,255,136,0.3)' },
    BEARISH: { bg: 'linear-gradient(135deg,#ff4444,#ff2222)', text: '#fff',    glow: 'rgba(255,68,68,0.3)'  },
    NEUTRAL: { bg: 'linear-gradient(135deg,#ffaa00,#ff8800)', text: '#050d1a', glow: 'rgba(255,170,0,0.3)'  },
  };
  const c = cfg[rec] || cfg.NEUTRAL;
  return (
    <div style={{ padding: '0.75rem 2rem', background: c.bg, borderRadius: '12px', fontSize: '1.5rem', fontWeight: 900, color: c.text, letterSpacing: '0.1em', boxShadow: `0 0 24px ${c.glow}`, textAlign: 'center' }}>
      {rec === 'BULLISH' ? '▲ ' : rec === 'BEARISH' ? '▼ ' : '◆ '}{rec}
    </div>
  );
};

// ─── Format helpers ─────────────────────────────────────────────────────────────
// AV returns growth/ROE as decimal (0.085 = 8.5%); Finnhub returns % (8.5). Detect by magnitude.
function fmtGrowth(av, finnhub) {
  const use = (av !== null && av !== undefined) ? av : finnhub;
  if (use === null || use === undefined) return null;
  const n = parseFloat(use);
  if (isNaN(n)) return null;
  const pct = Math.abs(n) < 5 ? n * 100 : n;
  return (pct >= 0 ? '+' : '') + pct.toFixed(1) + '%';
}
function fmtRoe(avRoe, finnhubRoe) {
  const use = (avRoe !== null && avRoe !== undefined) ? avRoe : finnhubRoe;
  if (use === null || use === undefined) return null;
  const n = parseFloat(use);
  if (isNaN(n)) return null;
  const pct = Math.abs(n) < 5 ? n * 100 : n;
  return pct.toFixed(1) + '%';
}
function fmtMargin(avMargin, finnhubMargin) {
  const use = (avMargin !== null && avMargin !== undefined) ? avMargin : finnhubMargin;
  if (use === null || use === undefined) return null;
  const n = parseFloat(use);
  if (isNaN(n)) return null;
  const pct = Math.abs(n) < 5 ? n * 100 : n;
  return pct.toFixed(1) + '%';
}

// ─── Main AnalysisResult ────────────────────────────────────────────────────────
const AnalysisResult = ({ analysis, onSave, token, navigateTo, inline = false }) => {
  const [showMethodology, setShowMethodology] = useState(false);
  const [copied,          setCopied]          = useState(false);
  const [shareLoading,    setShareLoading]    = useState(false);

  const price  = parseFloat(analysis.currentPrice)  || 0;
  const target = parseFloat(analysis.modelEstimate || analysis.priceTarget) || 0;
  const gain   = parseFloat(analysis.estimatedRange || analysis.potentialGain) || 0;
  const score  = parseFloat(analysis.score) || 50;
  const chg    = analysis.marketData?.change        || 0;
  const chgPct = analysis.marketData?.changePercent || 0;
  const isUp   = chg >= 0;
  const scoreColor = score >= 75 ? '#00ff88' : score >= 60 ? '#00d4ff' : score >= 40 ? '#ffaa00' : '#ff4444';

  // ── V3: real score components ────────────────────────────────────────────────
  const mqScore = analysis.mqScore ?? null;   // Momentum Quality 0-100
  const fqScore = analysis.fqScore ?? null;   // Fundamental Quality 0-100 (null = ETF)
  const fqGate  = analysis.fqGate  ?? null;
  const d5      = analysis.dimensions?.d5 ?? null;
  // Risk score: D5 (-18 to 0) → 0-100. 100 = no risk penalty (safest). 0 = full penalty.
  const riskScore = d5 !== null ? Math.max(0, Math.round(100 + (d5 / 18) * 100)) : null;
  const riskColor = riskScore !== null
    ? (riskScore >= 67 ? '#00ff88' : riskScore >= 34 ? '#ffaa00' : '#ff4444')
    : '#64748b';

  // ── V3: target range ─────────────────────────────────────────────────────────
  const targetLow  = parseFloat(analysis.targetLow)  || 0;
  const targetHigh = parseFloat(analysis.targetHigh) || 0;
  const hasV3Range = targetLow > 0 && targetHigh > 0;
  const midTarget  = hasV3Range ? (targetLow + targetHigh) / 2 : target;
  const actualGain = price > 0 && midTarget > 0
    ? Math.round(((midTarget - price) / price) * 100 * 10) / 10
    : gain;

  // ── V3: analyst target ───────────────────────────────────────────────────────
  const analystTP  = parseFloat(analysis.analystTargetPrice) || 0;
  const analystVsC = analysis.analystVsCurrent ?? null;

  // ── Fundamentals ─────────────────────────────────────────────────────────────
  const raw = analysis.fundamentals || analysis.marketData || {};
  const fd = {
    ...raw,
    peRatio:      raw.peRatio      ?? raw.pe,
    week52High:   raw.week52High   ?? raw.high52w,
    week52Low:    raw.week52Low    ?? raw.low52w,
    avgVolume:    raw.avgVolume    ?? raw.averageVolume,
    totalRevenue: raw.totalRevenue ?? raw.revenue,
  };

  const handleShare = async () => {
    setShareLoading(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = 'Bearer ' + token;
      const res = await fetch(`${API_URL}/api/share`, {
        method: 'POST', headers,
        body: JSON.stringify({ ticker: analysis.ticker, signal: analysis.recommendation, score: Math.round(score), price: price || null, companyName: analysis.companyName || analysis.ticker }),
      });
      const data = await res.json();
      if (data.success) {
        const url = `${window.location.origin}/share/${data.shareId}`;
        navigator.clipboard.writeText(url).catch(() => {});
        if (navigateTo) navigateTo('share', { shareId: data.shareId });
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      }
    } catch (e) { console.error('Share failed:', e); }
    setShareLoading(false);
  };

  const fmt = (n, prefix = '', suffix = '') => {
    if (!n && n !== 0) return '—';
    const num = parseFloat(n);
    if (isNaN(num)) return String(n);
    if (Math.abs(num) >= 1e12) return prefix + (num / 1e12).toFixed(2) + 'T' + suffix;
    if (Math.abs(num) >= 1e9)  return prefix + (num / 1e9).toFixed(2)  + 'B' + suffix;
    if (Math.abs(num) >= 1e6)  return prefix + (num / 1e6).toFixed(2)  + 'M' + suffix;
    return prefix + num.toFixed(2) + suffix;
  };

  // Derived display values — null means MetricCell will show '—'
  const growthDisplay = fmtGrowth(fd.revenueGrowthYOY, fd.revenueGrowthTTMYoy);
  const roeDisplay    = fmtRoe(fd.returnOnEquityTTM, fd.roeTTM);
  const marginDisplay = fmtMargin(fd.profitMargin, fd.netMarginTTM);
  const deRaw         = parseFloat(fd.debtEquityQuarterly);
  const deDisplay     = !isNaN(deRaw) && fd.debtEquityQuarterly !== null ? deRaw.toFixed(2) + 'x' : null;
  const deSub         = !isNaN(deRaw) ? (deRaw > 3 ? '⚠ High leverage' : deRaw < 0.3 ? '✓ Conservative' : undefined) : undefined;
  const fwdPE         = parseFloat(fd.forwardPE);
  const fwdPEDisplay  = !isNaN(fwdPE) && fd.forwardPE ? fwdPE.toFixed(1) + 'x' : null;
  const pbRaw         = parseFloat(fd.priceToBook);
  const pbDisplay     = !isNaN(pbRaw) && fd.priceToBook ? pbRaw.toFixed(2) + 'x' : null;
  const sma50Val      = parseFloat(fd.sma50);
  const sma200Val     = parseFloat(fd.sma200);
  const sma50Display  = !isNaN(sma50Val)  && sma50Val  > 0 ? '$' + sma50Val.toFixed(2)  : null;
  const sma200Display = !isNaN(sma200Val) && sma200Val > 0 ? '$' + sma200Val.toFixed(2) : null;
  const hasSMA        = sma50Display && sma200Display;
  const growthIsPositive = growthDisplay && !growthDisplay.startsWith('-');

  return (
    <div style={{ maxWidth: inline ? '100%' : '960px', margin: inline ? '0' : '0 auto', fontFamily: '"DM Sans", system-ui, sans-serif', borderRadius: inline ? '14px' : undefined, overflow: 'hidden', border: inline ? '1px solid rgba(0,212,255,0.15)' : 'none' }}>

      {/* ── Header (non-inline) ── */}
      {!inline && (
        <div style={{ background: 'linear-gradient(135deg,rgba(12,25,50,0.95),rgba(18,35,65,0.9))', border: '1px solid rgba(0,212,255,0.15)', borderRadius: '20px 20px 0 0', padding: '1.75rem 2rem', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1.5rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
              <h2 style={{ fontSize: '2rem', fontWeight: 900, color: '#f8fafc', margin: 0, letterSpacing: '-0.03em' }}>{analysis.ticker}</h2>
              <span style={{ fontSize: '0.8rem', color: '#475569', fontWeight: 600 }}>·</span>
              <span style={{ fontSize: '0.95rem', color: '#94a3b8', fontWeight: 500, maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{analysis.companyName}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '2.25rem', fontWeight: 900, color: '#f8fafc', letterSpacing: '-0.03em' }}>${price.toFixed(2)}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.25rem 0.7rem', background: isUp ? 'rgba(0,255,136,0.1)' : 'rgba(255,68,68,0.1)', borderRadius: '6px', border: `1px solid ${isUp ? 'rgba(0,255,136,0.2)' : 'rgba(255,68,68,0.2)'}` }}>
                {isUp ? <TrendingUp size={13} style={{ color: '#00ff88' }} /> : <TrendingDown size={13} style={{ color: '#ff4444' }} />}
                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: isUp ? '#00ff88' : '#ff4444' }}>
                  {isUp ? '+' : ''}{chg.toFixed(2)} ({isUp ? '+' : ''}{chgPct.toFixed(2)}%)
                </span>
              </div>
              <span style={{ fontSize: '0.7rem', color: '#334155' }}>today</span>
            </div>
            {analysis.dataSource && (
              <div style={{ fontSize: '0.68rem', color: '#334155', marginTop: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <Activity size={10} /> {analysis.dataSource}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap' }}>
            <ScoreRing score={score} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <RecBadge rec={analysis.recommendation} />
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={handleShare} disabled={shareLoading} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.5rem 0.75rem', background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: '8px', color: '#00d4ff', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, fontFamily: 'inherit' }}>
                  <Share2 size={13} /> {copied ? 'Copied!' : 'Share'}
                </button>
                {onSave && (
                  <button onClick={() => onSave(analysis)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.5rem 0.75rem', background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.2)', borderRadius: '8px', color: '#00ff88', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, fontFamily: 'inherit' }}>
                    <BookmarkPlus size={13} /> Save
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Key Metrics Grid ─────────────────────────────────────────────────── */}
      <div style={{ background: 'rgba(8,18,40,0.9)', border: '1px solid rgba(0,212,255,0.08)', borderTop: inline ? '1px solid rgba(0,212,255,0.08)' : 'none', padding: '1.5rem 2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
          <BarChart2 size={14} style={{ color: '#00d4ff' }} />
          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Key Metrics</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '0.6rem' }}>

          {/* Valuation */}
          <MetricSection label="Valuation" />
          <MetricCell label="Market Cap"   value={fmt(fd.marketCap || fd.market_cap, '$')} />
          <MetricCell label="P/E Ratio"    value={fd.peRatio || fd.pe_ratio ? parseFloat(fd.peRatio || fd.pe_ratio).toFixed(1) + 'x' : '—'} />
          {fwdPEDisplay  && <MetricCell label="Forward P/E"  value={fwdPEDisplay}  sub="next 12 months" />}
          {pbDisplay     && <MetricCell label="Price / Book" value={pbDisplay} />}
          <MetricCell label="EPS (TTM)"    value={fd.eps ? '$' + parseFloat(fd.eps).toFixed(2) : '—'} />
          <MetricCell label="Div Yield"    value={fd.dividendYield ? (parseFloat(fd.dividendYield) * 100).toFixed(2) + '%' : '—'} />

          {/* Profitability */}
          <MetricSection label="Profitability & Growth" />
          <MetricCell label="Revenue (TTM)"  value={fmt(fd.totalRevenue || fd.revenue, '$')} />
          {growthDisplay && <MetricCell label="Revenue Growth" value={growthDisplay} highlight={growthIsPositive} />}
          {marginDisplay && <MetricCell label="Profit Margin"  value={marginDisplay} />}
          {roeDisplay    && <MetricCell label="ROE (TTM)"      value={roeDisplay}    sub="return on equity" />}

          {/* Risk & Market */}
          <MetricSection label="Risk & Market" />
          <MetricCell label="Beta"       value={fd.beta ? parseFloat(fd.beta).toFixed(2) : '—'} sub="vs S&P 500" />
          {deDisplay && <MetricCell label="Debt / Equity" value={deDisplay} sub={deSub} />}
          <MetricCell label="Volume"     value={fd.volume ? fmt(fd.volume) : '—'} />
          <MetricCell label="Avg Volume" value={fd.avgVolume || fd.averageVolume ? fmt(fd.avgVolume || fd.averageVolume) : '—'} />
          <MetricCell label="52W High"   value={fd.week52High ? '$' + parseFloat(fd.week52High).toFixed(2) : '—'} />
          <MetricCell label="52W Low"    value={fd.week52Low  ? '$' + parseFloat(fd.week52Low).toFixed(2)  : '—'} />

          {/* Trend (only when SMA data available) */}
          {hasSMA && <MetricSection label="Trend Structure" />}
          {sma50Display  && <MetricCell label="SMA 50"  value={sma50Display}  sub={sma50Val > sma200Val ? '▲ above SMA200' : '▼ below SMA200'} />}
          {sma200Display && <MetricCell label="SMA 200" value={sma200Display} sub="long-term average" />}

          {/* MarketPulze Intelligence — unique to this platform */}
          <MetricSection label="MarketPulze Intelligence" />
          {analystTP > 0 && (
            <MetricCell
              label="Wall St. Target"
              value={'$' + analystTP.toFixed(2)}
              sub={analystVsC !== null ? (analystVsC >= 0 ? '+' : '') + analystVsC.toFixed(1) + '% vs current' : undefined}
              accent
            />
          )}
          {fqScore !== null && (
            <MetricCell
              label="Fund. Quality"
              value={fqScore + ' / 100'}
              sub={fqGate || undefined}
              highlight={fqScore >= 70}
              accent={fqScore >= 50 && fqScore < 70}
            />
          )}
          {hasV3Range ? (
            <MetricCell
              label="Model Range"
              value={'$' + targetLow.toFixed(0) + ' — $' + targetHigh.toFixed(0)}
              sub={actualGain !== 0 ? (actualGain >= 0 ? '+' : '') + actualGain.toFixed(1) + '% midpoint' : undefined}
              highlight
            />
          ) : target > 0 ? (
            <MetricCell label="Model Estimate" value={'$' + target.toFixed(2)} sub={`${gain >= 0 ? '+' : ''}${gain.toFixed(1)}% est. range`} highlight />
          ) : null}
        </div>
      </div>

      {/* ── Analysis text + Score Breakdown ─────────────────────────────────── */}
      <div style={{ background: 'rgba(8,18,40,0.85)', border: '1px solid rgba(0,212,255,0.08)', borderTop: 'none', padding: '1.5rem 2rem', display: 'grid', gridTemplateColumns: '1fr auto', gap: '2rem', alignItems: 'start' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.85rem' }}>
            <Activity size={14} style={{ color: '#00d4ff' }} />
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Algorithmic Analysis</span>
          </div>
          <p style={{ fontSize: '0.95rem', lineHeight: 1.75, color: '#cbd5e1', margin: 0 }}>{analysis.analysis}</p>
          <p style={{ fontSize: '0.68rem', color: '#334155', marginTop: '0.75rem', lineHeight: 1.6, padding: '0.6rem 0.85rem', background: 'rgba(255,170,0,0.04)', border: '1px solid rgba(255,170,0,0.1)', borderRadius: '6px' }}>
            ⚠️ <strong style={{ color: '#52525b' }}>Algorithmic output.</strong> This text is generated by automated algorithms, not a human analyst. It may contain errors or omissions and should not be relied upon as accurate or complete.
          </p>
        </div>

        {/* Score Breakdown — V3 real scores replacing fabricated multipliers */}
        <div style={{ minWidth: '200px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.85rem' }}>
            <Target size={14} style={{ color: '#00d4ff' }} />
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Score Breakdown</span>
          </div>
          {/* Overall — always the real combined score */}
          <SignalBar label="Overall" value={Math.round(score)} color={scoreColor} />
          {/* Momentum — real MQ from V3 algorithm */}
          <SignalBar
            label="Momentum"
            value={mqScore !== null ? Math.round(mqScore) : Math.round(score)}
            color="#00d4ff"
            sublabel={mqScore !== null ? 'real MQ' : undefined}
          />
          {/* Fundamentals — real FQ, or N/A for ETFs */}
          {fqScore !== null ? (
            <SignalBar
              label="Fundamentals"
              value={Math.round(fqScore)}
              color={fqScore >= 70 ? '#00ff88' : fqScore >= 50 ? '#ffaa00' : '#ff4444'}
              sublabel="real FQ"
            />
          ) : (
            <NASignalBar label="Fundamentals" reason="ETF / foreign" />
          )}
          {/* Risk — D5 dimension mapped to safety score (100 = safest) */}
          {riskScore !== null ? (
            <SignalBar label="Risk Score" value={riskScore} color={riskColor} sublabel="100=safe" />
          ) : (
            <NASignalBar label="Risk Score" />
          )}
        </div>
      </div>

      {/* ── Key Strengths + Risk Factors ─────────────────────────────────────── */}
      {(analysis.keyReasons?.length > 0 || analysis.risks?.length > 0) && (
        <div style={{ background: 'rgba(8,18,40,0.85)', border: '1px solid rgba(0,212,255,0.08)', borderTop: 'none', padding: '1.5rem 2rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
          {analysis.keyReasons?.length > 0 && (
            <div style={{ background: 'rgba(0,255,136,0.04)', border: '1px solid rgba(0,255,136,0.1)', borderRadius: '12px', padding: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.85rem' }}>
                <CheckCircle size={14} style={{ color: '#00ff88' }} />
                <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#00ff88', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Key Strengths</span>
              </div>
              {analysis.keyReasons.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.5rem', alignItems: 'flex-start' }}>
                  <span style={{ color: '#00ff88', fontSize: '0.7rem', marginTop: '4px', flexShrink: 0 }}>▶</span>
                  <span style={{ fontSize: '0.85rem', color: '#cbd5e1', lineHeight: 1.6 }}>{r}</span>
                </div>
              ))}
            </div>
          )}
          {analysis.risks?.length > 0 && (
            <div style={{ background: 'rgba(255,170,0,0.04)', border: '1px solid rgba(255,170,0,0.1)', borderRadius: '12px', padding: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.85rem' }}>
                <AlertTriangle size={14} style={{ color: '#ffaa00' }} />
                <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#ffaa00', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Risk Factors</span>
              </div>
              {analysis.risks.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.5rem', alignItems: 'flex-start' }}>
                  <span style={{ color: '#ffaa00', fontSize: '0.7rem', marginTop: '4px', flexShrink: 0 }}>▶</span>
                  <span style={{ fontSize: '0.85rem', color: '#cbd5e1', lineHeight: 1.6 }}>{r}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Price Target Banner — V3 range replaces V2 pinpoint ──────────────── */}
      {(hasV3Range || target > 0) && (
        <div style={{ background: 'linear-gradient(135deg,rgba(0,255,136,0.06),rgba(0,212,255,0.04))', border: '1px solid rgba(0,255,136,0.12)', borderTop: 'none', padding: '1.25rem 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ width: 40, height: 40, borderRadius: '10px', background: 'rgba(0,255,136,0.12)', border: '1px solid rgba(0,255,136,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DollarSign size={18} style={{ color: '#00ff88' }} />
            </div>
            <div>
              <div style={{ fontSize: '0.68rem', color: '#475569', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {hasV3Range ? 'Model Range (Algorithmic)' : 'Model Estimate (Algorithmic)'}
              </div>
              {hasV3Range ? (
                <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#00ff88', letterSpacing: '-0.03em', lineHeight: 1.1 }}>
                  ${targetLow.toFixed(2)} — ${targetHigh.toFixed(2)}
                </div>
              ) : (
                <div style={{ fontSize: '1.75rem', fontWeight: 900, color: '#00ff88', letterSpacing: '-0.03em', lineHeight: 1.1 }}>
                  ${target.toFixed(2)}
                </div>
              )}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.68rem', color: '#475569', letterSpacing: '0.06em', marginBottom: '0.2rem' }}>
              {hasV3Range ? 'MIDPOINT VS CURRENT' : `POTENTIAL ${actualGain >= 0 ? 'UPSIDE' : 'DOWNSIDE'}`}
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: 900, color: actualGain >= 0 ? '#00ff88' : '#ff4444', letterSpacing: '-0.02em' }}>
              {actualGain >= 0 ? '+' : ''}{actualGain.toFixed(1)}%
            </div>
          </div>
          <div style={{ flex: '0 0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.65rem', color: '#475569', letterSpacing: '0.06em' }}>CURRENT</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#94a3b8' }}>${price.toFixed(2)}</div>
              </div>
              <div style={{ fontSize: '1.2rem', color: '#334155' }}>→</div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.65rem', color: '#475569', letterSpacing: '0.06em' }}>{hasV3Range ? 'RANGE' : 'TARGET'}</div>
                <div style={{ fontSize: hasV3Range ? '0.82rem' : '1.1rem', fontWeight: 800, color: '#00ff88', lineHeight: 1.2 }}>
                  {hasV3Range ? `$${targetLow.toFixed(0)}–$${targetHigh.toFixed(0)}` : `$${target.toFixed(2)}`}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Scoring Methodology (collapsible) ───────────────────────────────── */}
      {analysis.scoringMethodology && (
        <div style={{ border: '1px solid rgba(0,212,255,0.08)', borderTop: 'none' }}>
          <button onClick={() => setShowMethodology(s => !s)}
            style={{ width: '100%', padding: '1rem 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(8,18,40,0.7)', border: 'none', color: '#64748b', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.82rem', fontWeight: 600 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Clock size={13} /> View Scoring Methodology
            </div>
            {showMethodology ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {showMethodology && (
            <div style={{ padding: '1.25rem 2rem', background: 'rgba(5,12,30,0.8)', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
              <pre style={{ fontSize: '0.75rem', color: '#64748b', whiteSpace: 'pre-wrap', fontFamily: '"JetBrains Mono", monospace', margin: 0, lineHeight: 1.7 }}>{analysis.scoringMethodology}</pre>
            </div>
          )}
        </div>
      )}

      {/* ── Disclaimer ───────────────────────────────────────────────────────── */}
      <div style={{ background: 'rgba(8,18,40,0.6)', border: '1px solid rgba(0,212,255,0.06)', borderTop: 'none', borderRadius: '0 0 20px 20px', padding: '1rem 2rem', display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
        <AlertTriangle size={13} style={{ color: '#475569', flexShrink: 0, marginTop: '2px' }} />
        <p style={{ fontSize: '0.67rem', color: '#475569', margin: 0, lineHeight: 1.7 }}>
          <strong style={{ color: '#64748b' }}>Not Financial Advice.</strong> This analysis is generated by automated algorithms for informational purposes only. MarketPulze is not a registered investment advisor. Scores and recommendations do not account for your personal financial situation. Always consult a qualified financial advisor before making investment decisions.
        </p>
      </div>
    </div>
  );
};

export default AnalysisResult;
